import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import type {
  LessonAiDraft,
  LessonWorkspaceMode,
  TeacherCohortAssignmentRecord,
  TeacherLessonRecord,
} from "../auth/api-client";
import {
  getLibraryLessons,
  patchTeacherLesson,
  postLessonAiGenerate,
  postLibraryLesson,
  postTeacherCohortAssignment,
  postTeacherLesson,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { DEFAULT_MAIN_PY, applyPep8IndentToLessonDraft, starterFilesFromMainPy } from "./course-template-helpers";
import LessonAiProgressPanel from "./LessonAiProgressPanel";
import { LessonAdvancedModePromptModal } from "./LessonAdvancedModePromptModal";
import {
  buildAdvancedPromptContext,
  draftForSimpleModeRegenerate,
  stripAdvancedModeMetadata,
  type LessonAiAdvancedPromptContext,
} from "./lessonAiAdvancedModePrompt";
import { LessonIcon } from "./LessonIcon";
import { startLessonAiProgressClock, type LessonAiProgressUpdate } from "./lesson-ai-progress";
import { saveLessonDraftToLibrary } from "./saveLessonDraft";
import { lessonPreviewAppearanceQuery, teacherPreviewLessonUrl } from "./teacherPreviewLessonUrl";
import { sortLessonLibrary } from "./lessonLibrarySort";
import { MentionInput } from "./standards-mentions/MentionInput";
import {
  emptyMentionDoc,
  isMentionDocEmpty,
  mentionDocFromPlainText,
  applyAiMentionsToDoc,
  toAiPromptText,
  toLessonAiStandardsRequest,
  type MentionPromptDoc,
} from "./standards-mentions/mentionTypes";
import { useDefaultStandardSetId } from "./standards-mentions/useDefaultStandardSetId";
import { useStandardsCatalog } from "./standards-mentions/useStandardsCatalog";
import { AI_SPEND_TOOLTIP, AiSpendIcon } from "./ai-spend";

const EMPTY_DRAFT: LessonAiDraft = {
  title: "",
  workspaceMode: "simple",
  mainPy: DEFAULT_MAIN_PY,
  tourSteps: [{ title: "Welcome", body: ["Follow the instructions in the editor."], referenceCode: null }],
  tests: [],
};

type AddLessonContext = "cohort" | "template";

export interface AddLessonFocusStandard {
  readonly standardSetId: string;
  readonly standardId: string;
  readonly prompt?: string;
}

export interface AddLessonModalProps {
  readonly open: boolean;
  readonly context: AddLessonContext;
  readonly courseId: string;
  readonly cohortId?: string;
  /** Cohort: null = start; undefined = end; uuid = after assignment. Template: same with lesson ids. */
  readonly insertAfterId: string | null | undefined;
  /** Template only: plan item id for return URLs (preserves quiz insert slot after lesson preview). */
  readonly insertAfterPlanItemId?: string | null | undefined;
  readonly sequenceLessons: readonly TeacherLessonRecord[];
  readonly assignments?: readonly TeacherCohortAssignmentRecord[];
  /** When set, library lessons covering this standard sort first and AI prompt is pre-filled. */
  readonly focusStandard?: AddLessonFocusStandard | null;
  readonly onClose: () => void;
  readonly onAdded: () => void | Promise<void>;
}

type ModalPhase = "pick" | "ai" | "saving" | "ready";

type NeighborLesson = {
  readonly lessonId: string;
  readonly title: string;
  readonly iconName?: string | null;
  readonly iconBgColor?: string | null;
  readonly iconFgColor?: string | null;
  readonly workspaceMode?: LessonWorkspaceMode;
};

function neighborFromAssignment(a: TeacherCohortAssignmentRecord): NeighborLesson | null {
  if (!a.lessonId) return null;
  return {
    lessonId: a.lessonId,
    title: a.lessonTitle,
    iconName: a.iconName,
    iconBgColor: a.iconBgColor,
    iconFgColor: a.iconFgColor,
    workspaceMode: a.workspaceMode,
  };
}

function neighborFromLesson(l: TeacherLessonRecord): NeighborLesson {
  return {
    lessonId: l.id,
    title: l.title,
    iconName: l.iconName,
    iconBgColor: l.iconBgColor,
    iconFgColor: l.iconFgColor,
    workspaceMode: l.workspaceMode,
  };
}

function insertNeighbors(
  context: AddLessonContext,
  insertAfterId: string | null | undefined,
  sequenceLessons: readonly TeacherLessonRecord[],
  assignments: readonly TeacherCohortAssignmentRecord[]
): { prev: NeighborLesson | null; next: NeighborLesson | null } {
  if (context === "cohort") {
    if (insertAfterId === null) {
      return assignments.length > 0
        ? { prev: null, next: neighborFromAssignment(assignments[0]) }
        : { prev: null, next: null };
    }
    if (insertAfterId === undefined) {
      return assignments.length > 0
        ? { prev: neighborFromAssignment(assignments[assignments.length - 1]), next: null }
        : { prev: null, next: null };
    }
    const idx = assignments.findIndex((a) => a.id === insertAfterId);
    if (idx < 0) return { prev: null, next: null };
    return {
      prev: neighborFromAssignment(assignments[idx]),
      next: idx + 1 < assignments.length ? neighborFromAssignment(assignments[idx + 1]) : null,
    };
  }

  if (insertAfterId === null) {
    return sequenceLessons.length > 0
      ? { prev: null, next: neighborFromLesson(sequenceLessons[0]) }
      : { prev: null, next: null };
  }
  if (insertAfterId === undefined) {
    return sequenceLessons.length > 0
      ? { prev: neighborFromLesson(sequenceLessons[sequenceLessons.length - 1]), next: null }
      : { prev: null, next: null };
  }
  const idx = sequenceLessons.findIndex((l) => l.id === insertAfterId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: neighborFromLesson(sequenceLessons[idx]),
    next: idx + 1 < sequenceLessons.length ? neighborFromLesson(sequenceLessons[idx + 1]) : null,
  };
}

function filterAvailableLessons(
  lessons: readonly TeacherLessonRecord[],
  usedLessonIds: ReadonlySet<string>,
  search: string,
  focusStandardId?: string
): TeacherLessonRecord[] {
  const q = search.trim().toLowerCase();
  const pool = lessons
    .filter((l) => !usedLessonIds.has(l.id))
    .filter((l) => !q || l.title.toLowerCase().includes(q));
  const sorted = sortLessonLibrary(pool, "title");
  if (!focusStandardId) return sorted;
  const matching = sorted.filter((l) => l.coversHighlightStandard);
  const rest = sorted.filter((l) => !l.coversHighlightStandard);
  return [...matching, ...rest];
}

export function AddLessonModal({
  open,
  context,
  courseId,
  cohortId,
  insertAfterId,
  insertAfterPlanItemId,
  sequenceLessons,
  assignments = [],
  focusStandard = null,
  onClose,
  onAdded,
}: AddLessonModalProps) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [phase, setPhase] = useState<ModalPhase>("pick");
  const [libraryLessons, setLibraryLessons] = useState<TeacherLessonRecord[]>([]);
  const [draftLessons, setDraftLessons] = useState<TeacherLessonRecord[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [mentionDoc, setMentionDoc] = useState<MentionPromptDoc>(emptyMentionDoc);
  const { setId: defaultSetId } = useDefaultStandardSetId(bearer, { courseId });
  const [standardSetId, setStandardSetId] = useState("");
  const catalog = useStandardsCatalog(bearer, standardSetId || defaultSetId);
  const activeSetId = standardSetId || defaultSetId;

  useEffect(() => {
    if (defaultSetId && !standardSetId) setStandardSetId(defaultSetId);
  }, [defaultSetId, standardSetId]);

  const [placeholderTitle, setPlaceholderTitle] = useState("");
  const [aiProgress, setAiProgress] = useState<LessonAiProgressUpdate | null>(null);
  const [draft, setDraft] = useState<LessonAiDraft>(EMPTY_DRAFT);
  const [savedLessonId, setSavedLessonId] = useState<string | null>(null);
  const [advancedPrompt, setAdvancedPrompt] = useState<LessonAiAdvancedPromptContext | null>(null);
  const [advancedPromptBusy, setAdvancedPromptBusy] = useState(false);
  const progressStopRef = useRef<(() => void) | null>(null);
  const focusSeededRef = useRef(false);

  const usedLessonIds = useMemo(() => {
    if (context === "template") {
      return new Set(sequenceLessons.map((l) => l.id));
    }
    return new Set(
      assignments.map((a) => a.lessonId).filter((id): id is string => typeof id === "string" && id.length > 0)
    );
  }, [assignments, context, sequenceLessons]);

  const availableLibraryLessons = useMemo(
    () => filterAvailableLessons(libraryLessons, usedLessonIds, search, focusStandard?.standardId),
    [libraryLessons, usedLessonIds, search, focusStandard?.standardId]
  );

  const availableDraftLessons = useMemo(
    () => filterAvailableLessons(draftLessons, usedLessonIds, search, focusStandard?.standardId),
    [draftLessons, usedLessonIds, search, focusStandard?.standardId]
  );

  const previewReturnTo = useMemo(() => {
    const qs = new URLSearchParams();
    if (context === "template") {
      if (insertAfterPlanItemId === null) qs.set("addLesson", "start");
      else if (insertAfterPlanItemId === undefined) qs.set("addLesson", "end");
      else if (insertAfterPlanItemId) qs.set("addLesson", `after-plan-${insertAfterPlanItemId}`);
    } else {
      if (insertAfterId === null) qs.set("addLesson", "start");
      else if (insertAfterId === undefined) qs.set("addLesson", "end");
      else qs.set("addLesson", `after-${insertAfterId}`);
    }
    if (context === "cohort" && cohortId) {
      return `/teacher/cohorts/${encodeURIComponent(cohortId)}?${qs.toString()}`;
    }
    return `/teacher/courses/${encodeURIComponent(courseId)}/manage?${qs.toString()}`;
  }, [cohortId, context, courseId, insertAfterId, insertAfterPlanItemId]);

  const previewUrl =
    savedLessonId && draft.title.trim()
      ? teacherPreviewLessonUrl({
          lessonId: savedLessonId,
          lessonTitle: draft.title,
          cohortId: context === "cohort" ? cohortId : undefined,
          courseId,
          returnTo: previewReturnTo,
          ...lessonPreviewAppearanceQuery(draft),
        })
      : null;

  const modalTitle = context === "template" ? "Add lesson to template" : "Add lesson to plan";
  const readyBlurb =
    context === "template"
      ? "Saved to your library. Preview in a new tab, then add it to this template sequence when you are ready."
      : "Saved to your library. Preview in a new tab, then add it to this cohort's plan when you are ready.";
  const addButtonLabel = context === "template" ? "Add to sequence" : "Add to lesson plan";
  const insertNeighborLessons = useMemo(
    () => insertNeighbors(context, insertAfterId, sequenceLessons, assignments),
    [assignments, context, insertAfterId, sequenceLessons]
  );
  const modalBlurb = useMemo(() => {
    if (phase === "ready") return readyBlurb;
    if (phase === "saving") return "Saving your lesson…";
    if (phase === "ai") {
      return "AI is drafting your lesson. This usually takes 20–45 seconds.";
    }
    if (focusStandard) {
      return "Adding a lesson for a specific standard. Pick from your library, create a placeholder, or draft with AI.";
    }
    return "Pick from your library, create a placeholder, or draft a specialized lesson with AI.";
  }, [focusStandard, phase, readyBlurb]);
  const emptyLibraryMessage =
    context === "template"
      ? "No available lessons. Everything in your library is already in this template."
      : "No available lessons. Everything in your library is already on this plan.";
  const libraryListEmptyMessage = search.trim() ? "No lessons match your search." : emptyLibraryMessage;

  const resetState = useCallback(() => {
    setPhase("pick");
    setSearch("");
    setError(null);
    setBusy(false);
    setMentionDoc(emptyMentionDoc());
    setStandardSetId("");
    setPlaceholderTitle("");
    setAiProgress(null);
    setDraft(EMPTY_DRAFT);
    setSavedLessonId(null);
    setAdvancedPrompt(null);
    setAdvancedPromptBusy(false);
    progressStopRef.current?.();
    progressStopRef.current = null;
  }, []);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
    if (open) {
      focusSeededRef.current = false;
      resetState();
      if (focusStandard?.standardSetId) setStandardSetId(focusStandard.standardSetId);
    }
  }, [open, resetState, focusStandard?.standardSetId]);

  useEffect(() => {
    if (!open || !bearer) return;
    let cancelled = false;
    setLoadingLibrary(true);
    void (async () => {
      try {
        const highlight =
          focusStandard?.standardId ? { highlightStandardId: focusStandard.standardId } : {};
        const [libraryData, draftData] = await Promise.all([
          getLibraryLessons(bearer, { limit: 200, ...highlight }),
          getLibraryLessons(bearer, { limit: 200, drafts: true, ...highlight }),
        ]);
        if (!cancelled) {
          setLibraryLessons(libraryData.lessons);
          setDraftLessons(draftData.lessons);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load library");
      } finally {
        if (!cancelled) setLoadingLibrary(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bearer, focusStandard?.standardId]);

  useEffect(() => {
    if (!open || !focusStandard?.standardId || focusSeededRef.current || catalog.loading) return;
    const std = catalog.standards.find((s) => s.id === focusStandard.standardId);
    if (!std) return;
    focusSeededRef.current = true;
    const baseText =
      focusStandard.prompt?.trim() ||
      `Create a lesson that teaches and assesses standard ${std.code}.`;
    let doc = mentionDocFromPlainText(baseText);
    doc = applyAiMentionsToDoc(doc, catalog.standards, catalog.glossary, [std.code], []);
    setMentionDoc(doc);
    if (focusStandard.standardSetId) setStandardSetId(focusStandard.standardSetId);
  }, [open, focusStandard, catalog.standards, catalog.glossary, catalog.loading]);

  useEffect(() => {
    return () => {
      progressStopRef.current?.();
    };
  }, []);

  async function addToTemplateSequence(lessonId: string) {
    if (!bearer || !courseId) return;
    // The server inserts the lesson into the unified plan at the requested slot.
    // `insertAfterPlanItemId` accepts a plan item id, null (start), or undefined (end);
    // for legacy callers it also resolves a bare lesson id.
    const anchor = insertAfterPlanItemId !== undefined ? insertAfterPlanItemId : insertAfterId;
    await postTeacherLesson(bearer, courseId, {
      lessonId,
      insertAfterPlanItemId: anchor,
    });
  }

  async function addLesson(lessonId: string, options?: { promoteFromDraft?: boolean }) {
    if (!bearer) return;
    setBusy(true);
    setError(null);
    try {
      if (options?.promoteFromDraft) {
        await patchTeacherLesson(bearer, undefined, lessonId, { isDraft: false });
      }
      if (context === "template") {
        await addToTemplateSequence(lessonId);
      } else {
        if (!cohortId) return;
        await postTeacherLesson(bearer, courseId, { lessonId });
        const insertAfterAssignmentId =
          insertAfterId === null || insertAfterId === undefined || assignments.some((a) => a.id === insertAfterId)
            ? insertAfterId
            : undefined;
        await postTeacherCohortAssignment(bearer, cohortId, {
          lessonId,
          insertAfterAssignmentId,
        });
      }
      await onAdded();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add lesson");
    } finally {
      setBusy(false);
    }
  }

  async function handlePickLesson(lesson: TeacherLessonRecord, fromDraft = false) {
    await addLesson(lesson.id, { promoteFromDraft: fromDraft || lesson.isDraft === true });
  }

  async function handlePlaceholderCreate() {
    if (!bearer) return;
    const title = placeholderTitle.trim();
    if (!title) {
      setError("Enter a lesson title.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { lesson } = await postLibraryLesson(bearer, {
        title,
        starterFilesJson: starterFilesFromMainPy(DEFAULT_MAIN_PY),
      });
      setSavedLessonId(lesson.id);
      setDraft({ ...EMPTY_DRAFT, title });
      setPhase("ready");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create lesson");
    } finally {
      setBusy(false);
    }
  }

  async function persistAiDraft(generated: LessonAiDraft, creationPrompt: string) {
    if (!bearer) return;
    const lessonId = await saveLessonDraftToLibrary(bearer, stripAdvancedModeMetadata(generated), {
      creationPrompt,
    });
    setDraft(stripAdvancedModeMetadata(generated));
    setSavedLessonId(lessonId);
    setPhase("ready");
  }

  async function handleAiGenerate() {
    if (!bearer) return;
    if (isMentionDocEmpty(mentionDoc)) {
      setError("Describe the lesson you want AI to create.");
      return;
    }
    const prompt = toAiPromptText(mentionDoc);
    const standardsReq = toLessonAiStandardsRequest(mentionDoc, activeSetId || undefined);
    setPhase("ai");
    setError(null);
    setBusy(true);
    setAdvancedPrompt(null);
    progressStopRef.current?.();
    progressStopRef.current = startLessonAiProgressClock("generate", setAiProgress);
    try {
      const { draft: generated } = await postLessonAiGenerate(bearer, {
        prompt,
        ...(standardsReq ?? {}),
      });
      progressStopRef.current?.();
      progressStopRef.current = null;
      setAiProgress(null);
      const normalized = applyPep8IndentToLessonDraft(generated);
      const promptCtx = buildAdvancedPromptContext(normalized, { sourcePrompt: prompt, kind: "generate" });
      if (promptCtx) {
        setAdvancedPrompt(promptCtx);
        setPhase("pick");
        return;
      }
      setPhase("saving");
      await persistAiDraft(normalized, prompt);
    } catch (e) {
      setError(e instanceof Error ? e.message : "AI generation failed");
      setPhase("pick");
    } finally {
      progressStopRef.current?.();
      progressStopRef.current = null;
      setAiProgress(null);
      setBusy(false);
    }
  }

  async function handleAcceptAdvancedMode() {
    if (!bearer || !advancedPrompt) return;
    setAdvancedPromptBusy(true);
    setError(null);
    try {
      await persistAiDraft(advancedPrompt.pendingDraft, advancedPrompt.sourcePrompt);
      setAdvancedPrompt(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save lesson");
    } finally {
      setAdvancedPromptBusy(false);
    }
  }

  async function handlePreferSimpleMode() {
    if (!bearer || !advancedPrompt) return;
    const ctx = advancedPrompt;
    const standardsReq = toLessonAiStandardsRequest(mentionDoc, activeSetId || undefined);
    setAdvancedPromptBusy(true);
    setError(null);
    setBusy(true);
    progressStopRef.current?.();
    progressStopRef.current = startLessonAiProgressClock("generate", setAiProgress);
    try {
      const { draft: regenerated } = await postLessonAiGenerate(bearer, {
        prompt: ctx.sourcePrompt,
        draft: draftForSimpleModeRegenerate(ctx.pendingDraft),
        forceWorkspaceMode: "simple",
        ...(standardsReq ?? {}),
      });
      progressStopRef.current?.();
      progressStopRef.current = null;
      setAiProgress(null);
      setPhase("saving");
      await persistAiDraft(applyPep8IndentToLessonDraft(regenerated), ctx.sourcePrompt);
      setAdvancedPrompt(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not regenerate for simple mode");
    } finally {
      progressStopRef.current?.();
      progressStopRef.current = null;
      setAiProgress(null);
      setAdvancedPromptBusy(false);
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <dialog
        ref={dialogRef}
        className="teacher-modal add-lesson-modal"
        onClose={onClose}
        onClick={(ev) => {
          if (ev.target === dialogRef.current && !busy) onClose();
        }}
      >
        <div className="teacher-modal-card add-lesson-modal-card" role="document">
          <h2 className="teacher-modal-title">{modalTitle}</h2>
          <p className="muted small add-lesson-modal-lede" style={{ marginTop: 0 }}>
            {modalBlurb}
          </p>

          {error ? (
            <div className="warn-banner" role="alert">
              {error}
            </div>
          ) : null}

          {phase === "pick" && focusStandard ? (
            <p className="add-lesson-focus-hint muted small">
              Lessons in your library that already cover this standard are listed first.
            </p>
          ) : null}

          {phase === "ai" && aiProgress ? (
            <LessonAiProgressPanel mode="generate" progress={aiProgress} />
          ) : null}

          {phase === "saving" ? (
            <div className="add-lesson-saving" role="status" aria-live="polite" aria-busy="true">
              <div className="teacher-ai-progress-spinner" aria-hidden="true" />
            </div>
          ) : null}

          {phase === "ready" && savedLessonId ? (
            <div className="add-lesson-ready">
              <div className="add-lesson-ready-stack">
                {insertNeighborLessons.prev ? (
                  <div className="add-lesson-neighbor-card">
                    <LessonIcon
                      lessonId={insertNeighborLessons.prev.lessonId}
                      title={insertNeighborLessons.prev.title}
                      iconName={insertNeighborLessons.prev.iconName}
                      bgColor={insertNeighborLessons.prev.iconBgColor}
                      fgColor={insertNeighborLessons.prev.iconFgColor}
                      workspaceMode={insertNeighborLessons.prev.workspaceMode}
                      size={2.25}
                    />
                    <span className="add-lesson-neighbor-card-title">{insertNeighborLessons.prev.title}</span>
                  </div>
                ) : null}
                <div className="add-lesson-ready-row add-lesson-ready-row--new">
                  <LessonIcon
                    lessonId={savedLessonId}
                    title={draft.title}
                    iconName={draft.iconName}
                    bgColor={draft.iconBgColor}
                    fgColor={draft.iconFgColor}
                    workspaceMode={draft.workspaceMode}
                    size={3}
                  />
                  <strong className="add-lesson-ready-title">{draft.title}</strong>
                </div>
                {insertNeighborLessons.next ? (
                  <div className="add-lesson-neighbor-card">
                    <LessonIcon
                      lessonId={insertNeighborLessons.next.lessonId}
                      title={insertNeighborLessons.next.title}
                      iconName={insertNeighborLessons.next.iconName}
                      bgColor={insertNeighborLessons.next.iconBgColor}
                      fgColor={insertNeighborLessons.next.iconFgColor}
                      workspaceMode={insertNeighborLessons.next.workspaceMode}
                      size={2.25}
                    />
                    <span className="add-lesson-neighbor-card-title">{insertNeighborLessons.next.title}</span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : phase === "pick" ? (
            <>
              <div className="add-lesson-create-row">
                <label className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <span className="field-label">Quick placeholder</span>
                  <input
                    className="input"
                    value={placeholderTitle}
                    onChange={(ev) => setPlaceholderTitle(ev.target.value)}
                    placeholder="Lesson title"
                    disabled={busy}
                  />
                </label>
                <button
                  type="button"
                  className="btn secondary"
                  disabled={busy || !placeholderTitle.trim()}
                  onClick={() => void handlePlaceholderCreate()}
                >
                  Create
                </button>
              </div>

              <div className="add-lesson-ai-row">
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <span className="field-label">Create with AI</span>
                  <MentionInput
                    bearer={bearer}
                    value={mentionDoc}
                    onChange={setMentionDoc}
                    standardSetId={activeSetId}
                    onStandardSetIdChange={setStandardSetId}
                    standardSets={catalog.sets}
                    showSetSelector={catalog.sets.length > 1}
                    disabled={busy}
                    minRows={2}
                    placeholder={
                      context === "template"
                        ? "Describe a lesson for this template. Type @ to tag a standard or glossary term."
                        : "Describe a lesson for this cohort. Type @ to tag a standard or glossary term."
                    }
                  />
                </div>
                <button
                  type="button"
                  className="btn primary"
                  disabled={busy || advancedPrompt !== null || isMentionDocEmpty(mentionDoc)}
                  title={AI_SPEND_TOOLTIP}
                  onClick={() => void handleAiGenerate()}
                >
                  <AiSpendIcon /> Generate
                </button>
              </div>

              <label className="field">
                <span className="field-label">From your library</span>
                <input
                  className="input"
                  value={search}
                  onChange={(ev) => setSearch(ev.target.value)}
                  placeholder="Search lessons…"
                  disabled={busy}
                />
              </label>

              <div className="add-lesson-library-list" aria-busy={loadingLibrary}>
                {loadingLibrary ? (
                  <p className="muted small">Loading library…</p>
                ) : availableLibraryLessons.length === 0 && availableDraftLessons.length === 0 ? (
                  <p className="muted small">{libraryListEmptyMessage}</p>
                ) : (
                  <>
                    {availableLibraryLessons.length > 0 ? (
                      <ul className="add-lesson-library-items">
                        {availableLibraryLessons.map((lesson) => (
                          <li key={lesson.id}>
                            <button
                              type="button"
                              className="add-lesson-library-item"
                              disabled={busy}
                              onClick={() => void handlePickLesson(lesson)}
                            >
                              <LessonIcon
                                lessonId={lesson.id}
                                title={lesson.title}
                                iconName={lesson.iconName}
                                bgColor={lesson.iconBgColor}
                                fgColor={lesson.iconFgColor}
                                workspaceMode={lesson.workspaceMode}
                                size={2.25}
                              />
                              <span className="add-lesson-library-item-title">{lesson.title}</span>
                              {lesson.coversHighlightStandard ? (
                                <span className="add-lesson-library-match-badge">Covers standard</span>
                              ) : null}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {availableDraftLessons.length > 0 ? (
                      <div className="add-lesson-draft-section">
                        <p className="add-lesson-draft-section-head">
                          <span className="add-lesson-draft-section-label">Drafts</span>
                          <span className="muted small add-lesson-draft-section-note">
                            Choosing a draft moves it to your library.
                          </span>
                        </p>
                        <ul className="add-lesson-library-items">
                          {availableDraftLessons.map((lesson) => (
                            <li key={lesson.id}>
                              <button
                                type="button"
                                className="add-lesson-library-item add-lesson-library-item--draft"
                                disabled={busy}
                                onClick={() => void handlePickLesson(lesson, true)}
                              >
                                <LessonIcon
                                  lessonId={lesson.id}
                                  title={lesson.title}
                                  iconName={lesson.iconName}
                                  bgColor={lesson.iconBgColor}
                                  fgColor={lesson.iconFgColor}
                                  workspaceMode={lesson.workspaceMode}
                                  size={2.25}
                                />
                                <span className="add-lesson-library-item-title">{lesson.title}</span>
                                {lesson.coversHighlightStandard ? (
                                  <span className="add-lesson-library-match-badge">Covers standard</span>
                                ) : null}
                                <span className="add-lesson-library-draft-badge">
                                  <FontAwesomeIcon icon={faWandMagicSparkles} fixedWidth aria-hidden />
                                  Draft
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : null}

          <div className="teacher-modal-actions">
            {phase === "ready" && savedLessonId ? (
              <>
                {previewUrl ? (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn secondary"
                  >
                    <FontAwesomeIcon icon={faPlay} /> Preview
                  </a>
                ) : null}
                <button
                  type="button"
                  className="btn primary"
                  disabled={busy}
                  onClick={() => void addLesson(savedLessonId)}
                >
                  {busy ? "Adding…" : addButtonLabel}
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  disabled={busy}
                  onClick={() => setPhase("pick")}
                >
                  Back
                </button>
              </>
            ) : null}
            <button type="button" className="btn secondary" disabled={busy} onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </dialog>

      <LessonAdvancedModePromptModal
        open={advancedPrompt !== null}
        lessonTitle={advancedPrompt?.pendingDraft.title ?? ""}
        rationale={advancedPrompt?.rationale ?? ""}
        busy={advancedPromptBusy || busy}
        onAcceptAdvanced={() => void handleAcceptAdvancedMode()}
        onPreferSimple={() => void handlePreferSimpleMode()}
      />
    </>
  );
}
